import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, X, AlertTriangle, CheckCircle2, XCircle, Edit2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SortHeader } from '../../components/SortHeader';
import { useSortableData } from '../../hooks/useSortableData';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVENUE_FIELDS = [
  { key: 'ecg_amt', label: 'ECG Amt' },
  { key: 'xray', label: 'X-Ray' },
  { key: 'lab', label: 'LAB' },
  { key: 'scan_amt', label: 'SCAN Amt' },
  { key: 'aaric_pharmacy', label: 'Aaric Pharmacy' },
  { key: 'physiotherapy', label: 'Physiotherapy' },
  { key: 'dressing_charge', label: 'Dressing Charge' },
  { key: 'inj_charge', label: 'Inj Charge' },
  { key: 'procedure_charges', label: 'Procedure Charges' },
  { key: 'nebulizer', label: 'Nebulizer' },
  { key: 'iv_charges', label: 'IV Charges' },
  { key: 'appointment_op', label: 'Appointment OP' },
  { key: 'consultation_morning_doctor', label: 'Morning Doctor' },
  { key: 'consultation_evening_doctor', label: 'Evening Doctor' },
  { key: 'consultation_night_doctor', label: 'Night Doctor' },
  { key: 'home_collection_charge', label: 'Home Collection Charge' },
  { key: 'due_paid', label: 'Due Paid' },
];

const EXPENSE_FIELDS = [
  { key: 'amt_in_box', label: 'Amt in Box (Cash)' },
  { key: 'amt_in_gpay_amc', label: 'Amt in GPay AMC' },
  { key: 'scan_dr', label: 'Scan Dr' },
  { key: 'echo_doctor', label: 'ECHO Doctor' },
  { key: 'grocery', label: 'Grocery' },
  { key: 'medical_bill_payment', label: 'Medical Bill Payment' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'keertana', label: 'Keertana' },
  { key: 'other_expenses', label: 'Other Expenses' },
  { key: 'due', label: 'Due' },
];

const emptyForm = () => {
  const f = {
    date: new Date().toISOString().split('T')[0],
    time: 'Morning',
    opening_balance: 0,
    comments: '',
    explanation_note: '',
  };
  REVENUE_FIELDS.forEach(rf => { f[rf.key] = 0; });
  EXPENSE_FIELDS.forEach(ef => { f[ef.key] = 0; });
  return f;
};

const computeLocally = (form) => {
  const opening = parseFloat(form.opening_balance) || 0;
  const revenueSum = REVENUE_FIELDS.reduce((s, rf) => s + (parseFloat(form[rf.key]) || 0), 0);
  const total = opening + revenueSum;
  const turnover = total - opening;
  const expenseKeys = ['amt_in_gpay_amc', 'scan_dr', 'echo_doctor', 'grocery', 'medical_bill_payment', 'maintenance', 'keertana', 'other_expenses', 'due'];
  const expenseSum = expenseKeys.reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);
  const required = total - expenseSum;
  const amtInBox = parseFloat(form.amt_in_box) || 0;
  const diff = Math.abs(amtInBox - required);
  const check = diff <= 50;
  return { total, turnover, required, check, diff };
};

const AccountModule = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff/accounts`, { withCredentials: true });
      setEntries(data);
    } catch { toast.error('Failed to load entries'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const computed = computeLocally(form);
  const needsExplanation = !computed.check;

  const { sorted, sortKey, sortDir, requestSort } = useSortableData(entries, 'date', 'desc');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsExplanation && !form.explanation_note.trim()) {
      toast.error('Cash difference exceeds Rs.50. Please provide an explanation note.');
      return;
    }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (typeof payload[k] === 'string' && !isNaN(payload[k]) && k !== 'date' && k !== 'time' && k !== 'comments' && k !== 'explanation_note') {
          payload[k] = parseFloat(payload[k]) || 0;
        }
      });
      if (editId) {
        await axios.put(`${API}/staff/accounts/${editId}`, payload, { withCredentials: true });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/staff/accounts`, payload, { withCredentials: true });
        toast.success('Entry submitted');
      }
      setForm(emptyForm());
      setEditId(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (entry) => {
    const f = { ...emptyForm() };
    Object.keys(f).forEach(k => {
      if (entry[k] !== undefined) f[k] = entry[k];
    });
    setForm(f);
    setEditId(entry.id);
    setShowForm(true);
  };

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Shift-End Accounts</h2>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={() => { setForm(emptyForm()); setEditId(null); setShowForm(true); }} className="bg-[#0A2540]" data-testid="add-account-btn">
            <Plus className="h-4 w-4 mr-2" /> New Entry
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A2540]">{editId ? 'Edit' : 'New'} Shift Entry</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => updateField('date', e.target.value)} required data-testid="acct-date" /></div>
                <div>
                  <Label>Shift</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.time} onChange={e => updateField('time', e.target.value)} data-testid="acct-shift">
                    <option value="Morning">Morning</option>
                    <option value="Evening">Evening</option>
                    <option value="Night">Night</option>
                  </select>
                </div>
                <div><Label>Opening Balance</Label><Input type="number" value={form.opening_balance} onChange={e => updateField('opening_balance', e.target.value)} data-testid="acct-opening" /></div>
              </div>
              <div className="border-t pt-3">
                <h3 className="text-sm font-semibold text-[#0A2540] mb-2">Revenue</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {REVENUE_FIELDS.map(rf => (
                    <div key={rf.key}>
                      <Label className="text-xs">{rf.label}</Label>
                      <Input type="number" value={form[rf.key]} onChange={e => updateField(rf.key, e.target.value)} className="h-9" data-testid={`acct-${rf.key}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t pt-3">
                <h3 className="text-sm font-semibold text-[#0A2540] mb-2">Payments & Expenses</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {EXPENSE_FIELDS.map(ef => (
                    <div key={ef.key}>
                      <Label className="text-xs">{ef.label}</Label>
                      <Input type="number" value={form[ef.key]} onChange={e => updateField(ef.key, e.target.value)} className="h-9" data-testid={`acct-${ef.key}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#F1F5F9] rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm"><span>Total</span><span className="font-bold">Rs.{computed.total.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span>Turnover</span><span className="font-bold">Rs.{computed.turnover.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span>Required (Expected Cash)</span><span className="font-bold">Rs.{computed.required.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span>Cash in Box</span><span className="font-bold">Rs.{(parseFloat(form.amt_in_box) || 0).toLocaleString()}</span></div>
                <div className={`flex items-center justify-between text-sm font-bold rounded-lg px-3 py-2 ${computed.check ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  <span className="flex items-center">{computed.check ? <CheckCircle2 className="h-4 w-4 mr-1" /> : <XCircle className="h-4 w-4 mr-1" />} Check</span>
                  <span>Diff: Rs.{computed.diff.toFixed(0)} {computed.check ? '(OK)' : '(Needs Explanation)'}</span>
                </div>
              </div>
              {needsExplanation && (
                <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-4">
                  <div className="flex items-center text-amber-700 text-sm font-medium mb-2">
                    <AlertTriangle className="h-4 w-4 mr-2" /> Cash difference exceeds Rs.50. Explanation required.
                  </div>
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Explain the difference..." value={form.explanation_note} onChange={e => updateField('explanation_note', e.target.value)} data-testid="acct-explanation" />
                </div>
              )}
              <div><Label>Comments</Label><Input value={form.comments} onChange={e => updateField('comments', e.target.value)} data-testid="acct-comments" /></div>
              <Button type="submit" className="w-full bg-[#0A2540]" data-testid="acct-submit">{editId ? 'Update' : 'Submit'} Entry</Button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9]">
              <tr>
                <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Shift" sortKey="time" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Opening" sortKey="opening_balance" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="right" />
                <SortHeader label="Total" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="right" />
                <SortHeader label="Turnover" sortKey="turnover" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="right" />
                <SortHeader label="Cash" sortKey="amt_in_box" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="right" className="hidden sm:table-cell" />
                <SortHeader label="Required" sortKey="required" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="right" className="hidden md:table-cell" />
                <SortHeader label="Check" sortKey="check" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} align="center" />
                <th className="px-3 py-3 text-left font-semibold text-[#0A2540] hidden lg:table-cell">Comments</th>
                <th className="px-3 py-3 text-right font-semibold text-[#0A2540]">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-[#94A3B8]">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-[#94A3B8]">No entries yet</td></tr>
              ) : sorted.map(entry => (
                <tr key={entry.id} className="hover:bg-[#F8FAFC]" data-testid={`acct-row-${entry.id}`}>
                  <td className="px-3 py-3 font-medium text-[#0A2540]">{entry.date}</td>
                  <td className="px-3 py-3">{entry.time}</td>
                  <td className="px-3 py-3 text-right">Rs.{(entry.opening_balance || 0).toLocaleString()}</td>
                  <td className="px-3 py-3 text-right font-bold">Rs.{(entry.total || 0).toLocaleString()}</td>
                  <td className="px-3 py-3 text-right">Rs.{(entry.turnover || 0).toLocaleString()}</td>
                  <td className="px-3 py-3 text-right hidden sm:table-cell">Rs.{(entry.amt_in_box || 0).toLocaleString()}</td>
                  <td className="px-3 py-3 text-right hidden md:table-cell">Rs.{(entry.required || 0).toLocaleString()}</td>
                  <td className="px-3 py-3 text-center">
                    {entry.check ? <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" /> : <XCircle className="h-5 w-5 text-red-500 mx-auto" />}
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell max-w-[150px] truncate text-[#475569]">{entry.comments}</td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => handleEdit(entry)} className="p-1.5 rounded-lg hover:bg-[#F1F5F9]" data-testid={`edit-acct-${entry.id}`}>
                      <Edit2 className="h-4 w-4 text-[#475569]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountModule;
